import React, { useEffect } from 'react';

import { Link } from 'react-router-dom';

import { SkinOutlined } from '@ant-design/icons';
import { IntegratedErps, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { SearchVendorDTO } from '@recurrency/core-api-schema/dist/search/getSearchVendors';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';

import { DownloadButton } from 'components/recipes/DownloadButton';
import { SearchFrame } from 'components/recipes/SearchFrame';
import { ValueFacet } from 'components/recipes/SearchFrame/types';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { formatPercent, formatUSD, getChangeColor, splitIfIdNameStr } from 'utils/formatting';
import { routes, useHashState } from 'utils/routes';
import { PersistedColumn, ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import {
  changeDollarColumn,
  dollarColumn,
  numberColumn,
  sortableDateColumn,
  sortableDollarWithCentsColumn,
  sortableNumberColumn,
  sortableStringColumn,
} from 'utils/tables';

import { SearchFrameHashState } from 'types/hash-state';

export const VendorListPage = () => {
  const { activeTenant } = useGlobalApp();

  const [hashState, setHashState] = useHashState<SearchFrameHashState>();

  /** Default to hiding vendors with no SKUs * */
  useEffect(() => {
    if (Object.keys(hashState).length === 0) {
      setHashState({
        where: {
          has_at_least_one_item: ['At least 1 SKU'],
        },
      });
    }
    // disable so it only fires once on initial mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns: (PersistedColumn<SearchVendorDTO> | null)[] = [
    {
      title: 'ID',
      dataIndex: 'vendor_id',
      settingKey: 'vendor_id',
      required: true,
      render: (id: string, record: SearchVendorDTO) => (
        <Link to={routes.purchasing.vendorDetails(id, { companyId: record.company_id })}>{id}</Link>
      ),
    },
    sortableStringColumn({
      title: 'Vendor',
      dataIndex: 'vendor_name',
      settingKey: 'vendor_name',
      required: true,
      sorter: true,
    }),

    sortableDateColumn({
      title: 'First PO Date',
      dataIndex: 'first_po_date',
      settingKey: 'first_po_date',
      sorter: true,
    }),
    sortableNumberColumn({
      title: (
        <InfoTooltip
          title="Unique items marked stockable at at least one location where this vendor is primary"
          useInfoIcon
        >
          Unique Items
        </InfoTooltip>
      ),
      dataIndex: 'unique_item_count',
      settingKey: 'unique_item_count',
      sorter: true,
    }),
    sortableNumberColumn({
      title: (
        <InfoTooltip
          title="Number of customers who have purchased an item where this vendor is primary in the last 12 months"
          useInfoIcon
        >
          Unique Customers Last 12M
        </InfoTooltip>
      ),
      dataIndex: 'unique_customer_count_12m',
      settingKey: 'unique_customer_count_12m',
      sorter: true,
    }),
    dollarColumn({
      title: 'Purchases Last 12M',
      dataIndex: 'purchases_last_12m',
      settingKey: 'purchases_last_12m',
      sorter: true,
    }),

    dollarColumn({
      title: 'Purchases YTD',
      dataIndex: 'purchases_ytd',
      settingKey: 'purchases_ytd',
      sorter: true,
    }),
    dollarColumn({
      title: 'Purchases LYTD',
      dataIndex: 'purchases_lytd',
      settingKey: 'purchases_lytd',
      sorter: true,
    }),
    sortableDollarWithCentsColumn({
      title: 'Deadstock Value',
      dataIndex: 'deadstock_value',
      settingKey: 'deadstock_value',
      sorter: true,
    }),

    sortableDollarWithCentsColumn({
      title: 'Overstock Value',
      dataIndex: 'overstock_value',
      settingKey: 'overstock_value',
      sorter: true,
    }),

    sortableDollarWithCentsColumn({
      title: (
        <InfoTooltip title="Total sales from items where this vendor is primary in the last 12 months" useInfoIcon>
          Total Sales Last 12M
        </InfoTooltip>
      ),
      dataIndex: 'total_sales_price_12m',
      settingKey: 'total_sales_price_12m',
      sorter: true,
      optional: true,
    }),
    activeTenant.erpType === IntegratedErps.P21
      ? {
          title: 'Company',
          dataIndex: 'company',
          settingKey: 'company',
          render: (company: string) => splitIfIdNameStr(company)?.name,
          optional: true,
        }
      : null,

    numberColumn({
      title: '∆% Purchases',
      dataIndex: 'purchases_delta_pct',
      settingKey: 'purchases_delta_pct',
      sorter: true,
      render: (pct: string) => {
        const formatted = formatPercent(pct, 0);
        return <span style={{ color: getChangeColor(formatted) }}>{formatted}</span>;
      },
    }),
    changeDollarColumn({
      title: '∆ Purchases',
      dataIndex: 'purchases_delta_raw',
      settingKey: 'purchases_delta_raw',
      sorter: true,
      align: 'right' as const,
      render: (amt: string) => <span style={{ color: getChangeColor(amt) }}>{formatUSD(amt)}</span>,
      optional: true,
    }),
    sortableNumberColumn({
      title: (
        <InfoTooltip title="% of this vendor's items at the item-location level considered deadstock" useInfoIcon>
          Deadstock
        </InfoTooltip>
      ),
      dataIndex: 'deadstock_percent',
      settingKey: 'deadstock_percent',
      sorter: true,
      align: 'right' as const,
      render: (val: number) => formatPercent(val / 100, 0),
      optional: true,
    }),

    sortableDollarWithCentsColumn({
      title: 'Total Gross Margin Last 12M',
      dataIndex: 'total_gross_margin_12m',
      settingKey: 'total_gross_margin_12m',
      sorter: true,
      optional: true,
    }),

    sortableNumberColumn({
      title: 'Deadstock Items',
      dataIndex: 'deadstock_count',
      settingKey: 'deadstock_count',
      sorter: true,
      optional: true,
    }),

    sortableNumberColumn({
      title: 'Overstock Items',
      dataIndex: 'overstock_count',
      settingKey: 'overstock_count',
      sorter: true,
      optional: true,
    }),

    sortableNumberColumn({
      title: 'Normal Stock Items',
      dataIndex: 'normal_stock_count',
      settingKey: 'normal_stock_count',
      sorter: true,
      optional: true,
    }),

    sortableDollarWithCentsColumn({
      title: 'Normal Stock Value',
      dataIndex: 'normal_stock_value',
      settingKey: 'normal_stock_value',
      sorter: true,
      optional: true,
    }),
  ].filter(truthy);

  const valueFacets: (ValueFacet<SearchVendorDTO> | null)[] = [
    {
      title: 'Item Count',
      icon: <SkinOutlined />,
      field: 'has_at_least_one_item',
      options: [
        {
          label: 'No Items',
          value: 'No SKUs',
        },
        {
          label: 'At least 1 Item',
          value: 'At least 1 SKU',
        },
      ],
    },
  ].filter(truthy);

  return (
    <SearchFrame<SearchVendorDTO>
      title="Vendors"
      indexName={SearchIndexName.Vendors}
      tableColumnsSettingKey={ViewSettingKey.VendorListPage}
      queryPlaceholder="Search vendors by ID or name"
      columns={columns.filter(truthy)}
      valueFacets={valueFacets.filter(truthy)}
      headerActions={(searchFrameContext) => (
        <>
          <DownloadButton
            getDownloadData={searchFrameContext.getDownloadData}
            recordType="Vendors"
            permission={TenantUserPermission.AccessPurchasingVendorsDownload}
            disabled={!searchFrameContext.canDownload}
          />
        </>
      )}
    />
  );
};
